import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalendarCheck, FaClock, FaHistory, FaTimes, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserGoldCalendar = ({ user }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [availability, setAvailability] = useState({});
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [activeTab, setActiveTab] = useState('book'); // 'book' | 'my-bookings'

  useEffect(() => {
    fetchData();
  }, [user?.email]);

  const fetchData = async () => {
    if (!user?.email) return;
    
    setLoading(true);
    try {
      const [availRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/gold-calendar/user/availability`),
        axios.get(`${API}/gold-calendar/user/my-bookings?user_email=${encodeURIComponent(user.email)}`)
      ]);
      setAvailability(availRes.data.by_date || {});
      setMyBookings(bookingsRes.data.bookings || []);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBook = async () => {
    if (!selectedSlot || !user?.email) return;
    
    setBooking(true);
    try {
      await axios.post(`${API}/gold-calendar/user/book?user_email=${encodeURIComponent(user.email)}&user_name=${encodeURIComponent(user.name || 'Usuario Gold')}`, {
        slot_id: selectedSlot.id,
        notes: notes || null
      });
      toast.success('¡Sesión reservada! Recibirás confirmación pronto.');
      setSelectedSlot(null);
      setSelectedDate(null);
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reservar');
    } finally {
      setBooking(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/gold-calendar/user/bookings/${bookingId}?user_email=${encodeURIComponent(user.email)}`);
      toast.success('Reserva cancelada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  const availableDates = Object.keys(availability);
  const slotsForSelectedDate = selectedDate ? availability[format(selectedDate, 'yyyy-MM-dd')] || [] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div 
          className="w-16 h-16 rounded-full animate-pulse"
          style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div 
        className="rounded-2xl p-6"
        style={{ 
          background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.2), rgba(0, 240, 255, 0.2))',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="flex items-center gap-4">
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
          >
            <FaCalendarCheck className="text-2xl text-white" />
          </div>
          <div>
            <h2 
              className="text-2xl font-bold text-white uppercase"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Sesiones Personalizadas
            </h2>
            <p className="text-gray-400">Como miembro Gold, puedes reservar sesiones de entrenamiento 1:1</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-3">
        <Button
          onClick={() => setActiveTab('book')}
          className={`rounded-full px-6 font-bold uppercase tracking-wider ${
            activeTab === 'book' 
              ? 'text-white' 
              : 'bg-transparent text-gray-400 hover:text-white border border-gray-700'
          }`}
          style={activeTab === 'book' ? { background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' } : {}}
        >
          <FaCalendarCheck className="mr-2" /> Reservar Sesión
        </Button>
        <Button
          onClick={() => setActiveTab('my-bookings')}
          className={`rounded-full px-6 font-bold uppercase tracking-wider ${
            activeTab === 'my-bookings' 
              ? 'text-white' 
              : 'bg-transparent text-gray-400 hover:text-white border border-gray-700'
          }`}
          style={activeTab === 'my-bookings' ? { background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' } : {}}
        >
          <FaHistory className="mr-2" /> Mis Reservas ({myBookings.length})
        </Button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'book' ? (
          <motion.div
            key="book"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="grid lg:grid-cols-2 gap-6"
          >
            {/* Calendario */}
            <Card className="bg-[#1a1a1a] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Selecciona una Fecha</CardTitle>
                <CardDescription className="text-gray-400">
                  Las fechas destacadas tienen horarios disponibles
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setSelectedSlot(null);
                    }}
                    locale={es}
                    disabled={(date) => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      return date < new Date(new Date().setHours(0, 0, 0, 0)) || !availableDates.includes(dateStr);
                    }}
                    className="rounded-xl border border-white/10 bg-[#111] text-white"
                    modifiers={{
                      available: (date) => availableDates.includes(format(date, 'yyyy-MM-dd'))
                    }}
                    modifiersStyles={{
                      available: { 
                        backgroundColor: 'rgba(0, 240, 255, 0.3)', 
                        borderRadius: '8px',
                        fontWeight: 'bold'
                      }
                    }}
                  />
                </div>

                {availableDates.length === 0 && (
                  <div className="text-center mt-6 p-4 rounded-xl" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                    <p className="text-gray-400">No hay horarios disponibles por ahora</p>
                    <p className="text-sm text-gray-500 mt-1">Vuelve a revisar pronto</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Selector de Horarios */}
            <Card className="bg-[#1a1a1a] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">
                  {selectedDate 
                    ? `Horarios para ${format(selectedDate, "d 'de' MMMM", { locale: es })}`
                    : 'Selecciona una fecha'
                  }
                </CardTitle>
                <CardDescription className="text-gray-400">
                  {selectedDate ? `${slotsForSelectedDate.length} horarios disponibles` : 'Elige una fecha del calendario'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!selectedDate ? (
                  <div className="text-center py-12">
                    <FaClock className="text-5xl mx-auto mb-4" style={{ color: '#00F0FF' }} />
                    <p className="text-gray-400">Selecciona una fecha para ver horarios</p>
                  </div>
                ) : slotsForSelectedDate.length === 0 ? (
                  <div className="text-center py-12">
                    <FaClock className="text-5xl mx-auto mb-4 opacity-50" style={{ color: 'var(--brand-primary)' }} />
                    <p className="text-gray-400">No hay horarios disponibles</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Lista de horarios */}
                    <div className="grid grid-cols-2 gap-3">
                      {slotsForSelectedDate.sort((a, b) => a.start_time.localeCompare(b.start_time)).map(slot => (
                        <button
                          key={slot.id}
                          onClick={() => setSelectedSlot(slot)}
                          className={`p-4 rounded-xl text-left transition-all ${
                            selectedSlot?.id === slot.id
                              ? 'ring-2 ring-[var(--brand-primary)]'
                              : ''
                          }`}
                          style={{ 
                            background: selectedSlot?.id === slot.id 
                              ? 'rgba(var(--brand-primary-glow), 0.3)' 
                              : 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <FaClock style={{ color: '#00F0FF' }} />
                            <span className="font-semibold text-white">
                              {slot.start_time} - {slot.end_time}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">1 hora de sesión</p>
                        </button>
                      ))}
                    </div>

                    {/* Formulario de reserva */}
                    {selectedSlot && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4 pt-4 border-t border-white/10"
                      >
                        <div 
                          className="p-4 rounded-xl"
                          style={{ background: 'rgba(0, 240, 255, 0.1)' }}
                        >
                          <p className="text-white font-semibold">
                            Sesión seleccionada: {selectedSlot.start_time} - {selectedSlot.end_time}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {format(selectedDate, "EEEE d 'de' MMMM, yyyy", { locale: es })}
                          </p>
                        </div>

                        <div>
                          <label className="text-sm text-gray-400 mb-2 block">
                            Notas o comentarios (opcional)
                          </label>
                          <Textarea
                            placeholder="¿Hay algo específico que quieras trabajar en esta sesión?"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none"
                            rows={3}
                          />
                        </div>

                        <Button
                          onClick={handleBook}
                          disabled={booking}
                          className="w-full rounded-full font-bold text-white py-6"
                          style={{ 
                            background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
                            boxShadow: '0 0 20px rgba(var(--brand-primary-glow), 0.4)'
                          }}
                        >
                          {booking ? 'Reservando...' : '✨ Confirmar Reserva'}
                        </Button>
                      </motion.div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="my-bookings"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="bg-[#1a1a1a] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Mis Reservas</CardTitle>
                <CardDescription className="text-gray-400">
                  Historial de tus sesiones personalizadas
                </CardDescription>
              </CardHeader>
              <CardContent>
                {myBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <FaHistory className="text-5xl mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
                    <p className="text-gray-400">No tienes reservas todavía</p>
                    <Button
                      onClick={() => setActiveTab('book')}
                      className="mt-4 rounded-full text-white"
                      style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                    >
                      Reservar Primera Sesión
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myBookings.map(booking => (
                      <div 
                        key={booking.id}
                        className="p-4 rounded-xl"
                        style={{ 
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge 
                                className={`${
                                  booking.status === 'pending' ? 'bg-yellow-500' :
                                  booking.status === 'confirmed' ? 'bg-green-500' :
                                  booking.status === 'cancelled' ? 'bg-red-500' :
                                  'bg-blue-500'
                                } text-white`}
                              >
                                {booking.status === 'pending' ? '⏳ Pendiente' :
                                 booking.status === 'confirmed' ? '✅ Confirmada' :
                                 booking.status === 'cancelled' ? '❌ Cancelada' :
                                 '🎉 Completada'}
                              </Badge>
                            </div>
                            <p className="text-xl font-bold text-white">
                              {format(parseISO(booking.date), "EEEE d 'de' MMMM", { locale: es })}
                            </p>
                            <p className="text-gray-400 flex items-center gap-2 mt-1">
                              <FaClock style={{ color: '#00F0FF' }} />
                              {booking.start_time} - {booking.end_time}
                            </p>
                            {booking.notes && (
                              <p className="text-gray-500 text-sm mt-2 italic">"{booking.notes}"</p>
                            )}
                          </div>
                          
                          {(booking.status === 'pending' || booking.status === 'confirmed') && (
                            <Button
                              size="sm"
                              onClick={() => handleCancelBooking(booking.id)}
                              className="bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full"
                            >
                              <FaTimes className="mr-1" /> Cancelar
                            </Button>
                          )}
                          
                          {booking.status === 'completed' && (
                            <FaCheckCircle className="text-3xl" style={{ color: '#CCFF00' }} />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserGoldCalendar;
